<template>
  <div>
    <AppToolbar
      :title="vessel ? vessel.name : ''"
      :prev-route="
        vessel && ($security.getUserVessels().length > 1 || isShipowner)
          ? {
              name: 'OrganizationShowMap',
              params: { id: $iriToId(vessel.organization['@id']) }
            }
          : null
      "
      :toggle-drawer="true"
      @toggleDrawer="$emit('toggleDrawer')"
    >
      <template slot="buttons">
        <ToolbarView
          :management-route="{
            name: 'VesselShow',
            params: { id: $route.params.id }
          }"
        ></ToolbarView>
      </template>
    </AppToolbar>

    <CollapseMapBtn v-if="!isLoading"></CollapseMapBtn>

    <div v-if="isLoading" class="mt-6 text-center">
      <v-progress-circular indeterminate size="25" color="primary"></v-progress-circular>
    </div>
    <template v-else>
      <div v-if="vessel && $vuetify.breakpoint.mdAndUp" class="px-3 pt-3 pb-0 align-center justify-start d-flex-alt">
        <v-avatar size="50" class="mr-2">
          <img v-if="vessel.image" :src="vessel.image.contentUrl + '?x-request=html'" :alt="vessel.name" />
          <img v-else src="../../assets/no-img.svg" :alt="vessel.name" />
        </v-avatar>
        <div>
          <h1 class="headline">{{ vessel.name }}</h1>
          <router-link
            v-if="vessel"
            :to="{
              name: 'OrganizationShowMap',
              params: { id: $iriToId(vessel.organization['@id']) }
            }"
            class="subheading no-decoration"
            >{{ vessel.organization.name }}</router-link
          >
        </div>
      </div>

      <v-container
        v-if="vessel && (vessel.mmsi || vessel.imo || vessel.callsign)"
        fluid
        class="px-3 pt-3 pb-0 text-center caption"
      >
        <v-row>
          <v-col v-if="vessel.mmsi" cols="4">
            <h3 class="body-2 grey--text">MMSI</h3>
            {{ vessel.mmsi }}
          </v-col>
          <v-col v-if="vessel.imo" cols="4">
            <h3 class="body-2 grey--text">IMO</h3>
            {{ vessel.imo }}
          </v-col>
          <v-col v-if="vessel.callsign" cols="4">
            <h3 class="body-2 grey--text">Call Sign</h3>
            {{ vessel.callsign }}
          </v-col>
        </v-row>
      </v-container>

      <v-container v-if="vessel && vessel.latestPosition" class="grey lighten-5 pa-3 mt-3">
        <h3 class="body-2 grey--text mb-3 text-capitalize-first">
          {{ vessel.latestPosition.observedAt | moment('from', 'now') }}
        </h3>
        <v-row class="text-center">
          <v-col cols="4">
            <div>
              <v-icon size="32">mdi-crosshairs-gps</v-icon>
            </div>
            <span class="caption">
              {{ vessel.latestPosition.latitude | toNauticalPosition('lat') }}
              <br />
              {{ vessel.latestPosition.longitude | toNauticalPosition('long') }}
            </span>
          </v-col>
          <v-col cols="4">
            <div>
              <v-icon size="32">mdi-arrow-up</v-icon>
            </div>
            <span v-if="vessel.latestPosition.speed" class="caption">
              <abbr title="Speed Over Ground">SOG</abbr>
              {{ vessel.latestPosition.speed }}
              <br />knots
            </span>
            <span v-else class="grey--text lighten-2">{{ $t('message.no_data') }}</span>
          </v-col>
          <v-col cols="4">
            <div>
              <v-icon
                v-if="vessel.latestPosition.trueHeading"
                size="32"
                :style="{
                  transform: 'rotate(' + vessel.latestPosition.trueHeading + 'deg'
                }"
                >mdi-navigation</v-icon
              >
              <v-icon
                v-else-if="vessel.latestPosition.cog"
                size="32"
                :style="{
                  transform: 'rotate(' + vessel.latestPosition.cog + 'deg'
                }"
                >mdi-navigation</v-icon
              >
              <v-icon v-else size="32">mdi-navigation</v-icon>
            </div>
            <template v-if="vessel.latestPosition.trueHeading || vessel.latestPosition.cog">
              <span v-if="vessel.latestPosition.cog" class="caption">
                <abbr title="Course Over Ground">COG</abbr>
                {{ vessel.latestPosition.cog | toCompass }}
              </span>
              <span v-if="vessel.latestPosition.trueHeading" class="caption">
                <br v-if="vessel.latestPosition.cog" />
                <abbr title="True Heading">THDG</abbr>
                {{ vessel.latestPosition.trueHeading | toCompass }}
              </span>
            </template>
            <span v-else class="grey--text lighten-2">{{ $t('message.no_data') }}</span>
          </v-col>
        </v-row>
      </v-container>

      <div v-if="vessel" class="px-3">
        <v-switch
          v-if="vessel.latestPosition && $moment().diff(vessel.latestPosition.observedAt, 'days') <= 7"
          v-model="showPaths"
          :disabled="isLoadingPaths || isLoadingVessel"
          :label="$t('message.show_vessel_path_on_map')"
        ></v-switch>
        <template v-if="vessel.elbFailoverAvailable">
          <v-switch
            v-model="elbFailoverEnable"
            class="mt-0"
            :label="$t('message.enable_elb_failover')"
            :loading="elbFailoverEnableLoading"
          ></v-switch>
          <v-btn text block color="primary" @click="elbDialog = true">{{
            $t('message.what_is_the_elb_failover')
          }}</v-btn>
          <v-dialog v-model="elbDialog" width="500">
            <v-card>
              <v-card-title class="headline" primary-title>{{ $t('message.what_is_the_elb_failover') }}</v-card-title>

              <v-card-text>{{ $t('message.elb_failover_info') }}</v-card-text>

              <v-divider></v-divider>

              <v-card-actions>
                <div class="flex-grow-1"></div>
                <v-btn color="primary" text @click="elbDialog = false">{{ $t('message.close') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </div>

      <v-divider class="my-2"></v-divider>

      <div v-if="vessel && vessel.connectAgreements && vessel.connectAgreements.length > 0" class="px-3">
        <v-btn
          block
          target="_blank"
          :href="connectHost + '/agreement/' + encodeURIComponent($iriToId(vessel.connectAgreements[0].id))"
          text
          color="primary"
        >
          <v-avatar size="20" tile class="mr-2">
            <img src="https://file.usail.es/static/connect-icon-black.svg" alt="Fleet Commander" />
          </v-avatar>
          {{ $t('message.show_in_connect') }}</v-btn
        >
      </div>
      <div class="sidenav-body">
        <div v-if="roleInVessels && roleInVessels.length > 0">
          <v-list two-line>
            <v-subheader
              ><v-row class="align-center">
                <v-col cols="6">{{ $t('message.crewmen') }}</v-col>
                <v-col cols="6">
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text :disabled="pdfLoading" :loading="pdfLoading" small @click="downloadCards">
                    <v-icon>mdi-download</v-icon>
                    Download cards
                  </v-btn>
                </v-col>
              </v-row>
            </v-subheader>
            <v-text-field
              v-model="searchQuery"
              :placeholder="$t('message.search_by_name')"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
              class="mx-3 mb-3"
              solo
            ></v-text-field>
            <template v-for="(roleInVessel, index) in filteredRoleInVessels">
              <v-divider v-if="index > 0" :key="'riv' + index"></v-divider>
              <v-list-item :key="roleInVessel.id" @click="selectUser(roleInVessel.user)">
                <v-list-item-avatar>
                  <v-icon
                    v-if="false === roleInVessel.user.emailVerified"
                    class="grey lighten-4 grey--text text--lighten-2"
                    >mdi-email-outline</v-icon
                  >
                  <img
                    v-else-if="roleInVessel.user.image"
                    :src="roleInVessel.user.image.contentUrl + '?x-request=html'"
                    :alt="roleInVessel.user.firstName + ' ' + roleInVessel.user.lastName"
                  />
                  <img
                    v-else
                    src="../../assets/no-img.svg"
                    :alt="roleInVessel.user.firstName + ' ' + roleInVessel.user.lastName"
                  />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title
                    >{{ roleInVessel.user.firstName }} {{ roleInVessel.user.lastName }}</v-list-item-title
                  >
                  <v-list-item-subtitle>{{
                    roleInVessel.user.emailVerified
                      ? $t('message.' + roleInVessel.vesselRole.name)
                      : $t('message.accept_invitation_pending')
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </div>
        <div v-else class="empty-state-sm">
          <v-icon size="64" class="grey--text text--lighten-2">mdi-account-supervisor-circle</v-icon>
          <p class="grey--text">
            {{ $t('message.there_are_no_crew_members_yet') }}
          </p>
          <v-btn
            v-if="isShipowner || isCaptain || isFleetManager || isOfficer"
            text
            color="primary"
            @click="showUserShowDialog = true"
            >{{ $t('message.add_a_crew_member_now') }}</v-btn
          >
        </div>
      </div>

      <div
        :class="{
          'sidenav-speed-dial': true,
          'sidenav-speed-dial-sm': $vuetify.breakpoint.smAndDown
        }"
      >
        <v-speed-dial v-if="isShipowner || isCaptain || isFleetManager || isOfficer" v-model="fab">
          <template v-slot:activator>
            <v-btn color="orange" dark fab class="ma-0">
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else>mdi-plus</v-icon>
            </v-btn>
          </template>

          <v-btn style="position: relative;" fab dark small color="green" @click="showUserShowDialog = true">
            <v-icon>mdi-account-plus</v-icon>
            <div class="speed-dial-tooltip elevation-1">
              {{ $t('message.new_user') }}
            </div>
          </v-btn>

          <v-btn style="position: relative;" fab dark small color="blue" @click="showRoleInVesselDialog = true">
            <v-icon>mdi-account-check</v-icon>
            <div class="speed-dial-tooltip elevation-1">
              {{ $t('message.associate_employee') }}
            </div>
          </v-btn>
        </v-speed-dial>
      </div>

      <VesselManage v-if="vessel" :show="showManageDialog" :vessel="vessel"></VesselManage>
      <UserDialog
        :show="showUserShowDialog"
        :user="selectedUser"
        :vessel="vessel"
        @close="showUserShowDialog = false"
      ></UserDialog>
      <RoleInVesselDialog
        :show="showRoleInVesselDialog"
        :vessel="vessel"
        @close="showRoleInVesselDialog = false"
      ></RoleInVesselDialog>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import VesselManage from './ManageDialog'
import UserDialog from '@/components/user/dialog/Dialog'
import AppToolbar from '@/components/AppToolbar'
import fetch from '@/utils/fetch'
import CollapseMapBtn from '@/components/CollapseMapBtn'
import RoleInVesselDialog from '@/components/roleinvessel/dialog/Dialog'
import ToolbarView from '@/components/ToolbarView'

export default {
  name: 'VesselShowMap',

  components: {
    ToolbarView,
    RoleInVesselDialog,
    CollapseMapBtn,
    VesselManage,
    UserDialog,
    AppToolbar
  },

  filters: {
    toNauticalPosition: function(value, component = 'lat') {
      const abs = Math.abs(value)
      const int = Math.trunc(abs)
      let direction = ''

      const degrees = int.toString().padStart('lat' === component ? 2 : 3, '0')
      const minutesArray = ((abs - int) * 60).toFixed(1).split('.')
      const minutes = minutesArray[0].padStart(2, '0') + '.' + minutesArray[1]

      if ('lat' === component) {
        direction = value >= 0 ? 'N' : 'S'
      } else {
        direction = value >= 0 ? 'E' : 'W'
      }

      return degrees + 'º ' + minutes + "'" + ' ' + direction
    },

    toCompass: function(degrees) {
      return (
        Math.round(degrees)
          .toString()
          .padStart(3, '0') +
        'º ' +
        ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW', 'N'][
          Math.round(degrees / 11.25 / 2)
        ]
      )
    }
  },

  data() {
    return {
      showManageDialog: false,
      showUserShowDialog: false,
      showVesselUpdateDialog: false,
      showRoleInVesselDialog: false,
      selectedUser: null,
      isShipowner: false,
      isCaptain: false,
      pdfLoading: false,
      isFleetManager: false,
      isOfficer: false,
      connectHost: process.env.VUE_APP_CONNECT_HOST,
      elbDialog: false,
      elbFailoverEnable: false,
      elbFailoverEnableLoading: false,
      searchQuery: null,
      fab: false,
      tooltips: false,
      tooltipsDisabled: false
    }
  },

  computed: {
    ...mapGetters({
      error: 'roleinvessel/list/error',
      roleInVessels: 'roleinvessel/list/items',
      roleInVesselCreated: 'roleinvessel/create/created',
      isLoading: 'roleinvessel/list/isLoading',
      view: 'roleinvessel/list/view',
      vessel: 'vessel/show/retrieved',
      user: 'user/show/retrieved',
      vesselUpdated: 'vessel/update/updated'
    }),

    showPaths: {
      get() {
        return this.$store.state.map.showPaths
      },
      set(val) {
        this.$store.commit('mapSetShowPaths', val)
      }
    },

    isLoadingPaths: function() {
      return this.$store.state.map.isLoadingPaths
    },

    isLoadingVessel: function() {
      return this.$store.state.map.isLoadingVessel
    },

    filteredRoleInVessels: function() {
      if (!this.roleInVessels) {
        return null
      }
      let filteredRoleInVessels = this.roleInVessels
      if (this.searchQuery) {
        filteredRoleInVessels = this.roleInVessels.filter(roleInVessel => {
          const fullName = roleInVessel.user.firstName + ' ' + roleInVessel.user.lastName
          return fullName.toLocaleLowerCase().includes(this.searchQuery.toLowerCase())
        })
      }
      return filteredRoleInVessels
    }
  },

  watch: {
    $route() {
      this.getPage('/role_in_vessels?vessel=' + this.$route.params.id)
      setInterval(() => {
        this.retrieveVessel('/vessels/' + this.$route.params.id)
      }, 60000)
      this.retrieveVessel('/vessels/' + this.$route.params.id)
    },

    vessel: function(vessel) {
      if (!vessel || !vessel.organization) {
        return
      }

      this.isShipowner = this.$security.isGranted(vessel.organization, 'ROLE_SHIPOWNER')
      this.isCaptain = this.$security.isGranted(vessel, 'ROLE_CAPTAIN')
      this.isFleetManager = this.$security.isGranted(vessel, 'ROLE_FLEET_MANAGER')
      this.isOfficer = this.$security.isGranted(vessel, 'ROLE_OFFICER')

      this.elbFailoverEnable = vessel.elbFailoverEnable
    },

    vesselUpdated: function(vesselUpdate) {
      if (!vesselUpdate) {
        return
      }
      this.retrieveVessel('/vessels/' + this.$route.params.id)
      this.getPage('/role_in_vessels?vessel=' + this.$route.params.id)
    },

    roleInVesselCreated: function(roleInVesselCreated) {
      if (!roleInVesselCreated) {
        return
      }
      this.getPage('/role_in_vessels?vessel=' + this.$route.params.id)
    },

    elbFailoverEnable: function(elbFailoverEnable) {
      this.elbFailoverEnableLoading = true

      fetch(this.vessel['@id'], {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/ld+json' }),
        body: JSON.stringify({ elbFailoverEnable: elbFailoverEnable })
      })
        .then(response => response.json())
        .then(() => {
          this.elbFailoverEnableLoading = false
          this.vessel.elbFailoverEnable = elbFailoverEnable
        })
        .catch(() => {
          this.elbFailoverEnableLoading = false
          this.elbFailoverEnable = this.vessel.elbFailoverEnable
        })
    },

    showUserShowDialog: function(showUserShowDialog) {
      if (false === showUserShowDialog) {
        this.selectedUser = null
      }
    },

    fab(val) {
      this.tooltips = false
      this.tooltipsDisabled = false
      val &&
        setTimeout(() => {
          this.tooltips = true
          this.$nextTick(() => (this.tooltipsDisabled = true))
        }, 250)
    }
  },

  created() {
    this.$store.commit('mapSetShow', true)
    this.getPage('/role_in_vessels?pagination=false&vessel=' + this.$route.params.id)
    setInterval(() => {
      this.retrieveVessel('/vessels/' + this.$route.params.id + '?groups[]=fleet-commander-show')
    }, 60000)
    this.retrieveVessel('/vessels/' + this.$route.params.id + '?groups[]=fleet-commander-show')
  },

  methods: {
    ...mapActions({
      getPage: 'roleinvessel/list/default',
      retrieveVessel: 'vessel/show/retrieve',
      reset: 'vessel/show/reset'
    }),
    downloadCards() {
      this.pdfLoading = true
      fetch('/sites/' + this.$route.params.id + '/hotspot_user_pdf')
        .then(res => res.blob())
        .then(data => {
          let url = window.URL.createObjectURL(data)
          let a = document.createElement('a')
          a.href = url
          a.target = '_blank'
          a.click()
          this.pdfLoading = false
        })
    },
    selectUser: function(user) {
      this.selectedUser = user['@id']
      this.showUserShowDialog = true
    }
  }
}
</script>

<style lang="scss" scoped>
.speed-dial-tooltip {
  position: absolute;
  right: 50px;
  padding: 8px 15px;
  background: white;
  color: rgba(0, 0, 0, 0.87);
  text-transform: none;
  font-weight: normal;
  letter-spacing: normal;
  border-radius: 3px;
}
</style>
